import React, { useState, useEffect } from "react";
import { FaSignInAlt, FaSignOutAlt, FaUser, FaSearch, FaTimes, FaEdit, FaHistory, FaDownload, FaClock, FaInfoCircle, FaCalendarAlt  } from "react-icons/fa";
import { FiAlertTriangle, FiSunrise, FiSunset, FiArrowDownCircle, FiCoffee } from "react-icons/fi";
import Swal from "sweetalert2";
import useAxios from "../utils/useAxios";
import { useSidebar } from "../context/SidebarContext";
import OrganisationSelector from "../components/OrganisationSelector";
import styles from '../styles/PointageManuelPage';
import { useAuth } from '../context/AuthContext';

const PointageManuelPage = () => {
  const api = useAxios();
  const { sidebarVisible } = useSidebar();

  const [employes, setEmployes] = useState([]);
  const [pointages, setPointages] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [cardsPagination, setCardsPagination] = useState({ totalPages: 1, currentPage: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [periodeFilter, setPeriodeFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [motifModal, setMotifModal] = useState(false);
  const [modificationModal, setModificationModal] = useState(false);
  const [employeSelectionne, setEmployeSelectionne] = useState(null);
  const [pointageSelectionne, setPointageSelectionne] = useState(null);
  const [typePointage, setTypePointage] = useState("");
  const [motifSortie, setMotifSortie] = useState("");
  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const [periodePointage, setPeriodePointage] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedImage, setSelectedImage] = useState(null);
  const [motifDetailModal, setMotifDetailModal] = useState({ show: false, name: '', motif: '' });
  const { user } = useAuth();

  const [parametres, setParametres] = useState(null);
  const [loadingParametres, setLoadingParametres] = useState(true);

  const [ajoutModal, setAjoutModal] = useState(false);
  const [nouveauPointage, setNouveauPointage] = useState({
    employe: "",
    type_pointage: "entree",
    periode: "autre",
    motif_sortie: "",
    date: selectedDate,
    heure: new Date().toISOString().slice(11, 16),
  });

  const CARDS_PER_PAGE = 6;

  useEffect(() => {
    const fetchParametres = async () => {
      setLoadingParametres(true);
      try {
        const { data: parametresData } = await api.get('/pointage/parametres-pointage/');
        if (parametresData.length > 0) {
          setParametres(parametresData[0]);
        } else {
          // Fallback avec des valeurs par défaut
          setParametres({
            pause_debut: "12:30:00",
            pause_fin: "14:00:00", 
            heure_sortie_min: "16:00:00"
          });
        }
      } catch (error) {
        console.error('Erreur chargement paramètres', error);
        // Fallback en cas d'erreur
        setParametres({
          pause_debut: "12:30:00",
          pause_fin: "14:00:00", 
          heure_sortie_min: "16:00:00"
        });
      } finally {
        setLoadingParametres(false);
      }
    };
    fetchParametres();
  }, []);

  // Fonction pour déterminer la période basée sur les paramètres
  const determinerPeriode = (heurePointage = null) => {
    const heure = heurePointage ? new Date(`2000-01-01T${heurePointage}`) : new Date();
    
    if (!parametres) {
      return 'matin'; // Fallback par défaut
    }

    try {
      // Convertir les strings de temps en Date objects
      const pauseDebut = new Date(`2000-01-01T${parametres.pause_debut}`);
      const pauseFin = new Date(`2000-01-01T${parametres.pause_fin}`);
      const heureSortieMin = new Date(`2000-01-01T${parametres.heure_sortie_min}`);
      
      const currentMinutes = heure.getHours() * 60 + heure.getMinutes();
      const pauseDebutMinutes = pauseDebut.getHours() * 60 + pauseDebut.getMinutes();
      const pauseFinMinutes = pauseFin.getHours() * 60 + pauseFin.getMinutes();
      const heureSortieMinMinutes = heureSortieMin.getHours() * 60 + heureSortieMin.getMinutes();
      
      // Logique de détermination des périodes
      if (currentMinutes < pauseDebutMinutes) {
        return 'matin';
      } else if (currentMinutes >= pauseDebutMinutes && currentMinutes <= pauseFinMinutes) {
        return 'pause';
      } else if (currentMinutes > pauseFinMinutes && currentMinutes < heureSortieMinMinutes) {
        return 'apres_midi';
      } else {
        return 'descente';
      }
      
    } catch (error) {
      console.error('Erreur dans determinerPeriode:', error);
      return 'matin';
    }
  };

  // Vérifier si un motif est nécessaire 
  const verifierMotifNecessaire = (heurePointage = null) => {
    // Toujours retourner false pour les entrées
    if (typePointage === 'entree') {
      return false;
    }
    
    if (!parametres) {
      return true; 
    }
    
    try {
      const now = new Date();
      const heure = heurePointage ? 
        new Date(`${now.toDateString()} ${heurePointage}`) : 
        now;
      
      // Convertir les heures des paramètres en minutes depuis minuit pour une comparaison facile
      const timeToMinutes = (timeStr) => {
        if (!timeStr) return null;
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };
      
      const currentMinutes = heure.getHours() * 60 + heure.getMinutes();
      const pauseDebutMinutes = timeToMinutes(parametres.pause_debut);
      const pauseFinMinutes = timeToMinutes(parametres.pause_fin);
      const heureSortieMinMinutes = timeToMinutes(parametres.heure_sortie_min);
      
      
      // 1. Si c'est pendant la pause -> pas de motif
      if (pauseDebutMinutes !== null && pauseFinMinutes !== null && 
          currentMinutes >= pauseDebutMinutes && currentMinutes <= pauseFinMinutes) {
        return false;
      }
      
      // 2. Si c'est après l'heure de sortie minimale -> pas de motif
      if (heureSortieMinMinutes !== null && currentMinutes >= heureSortieMinMinutes) {
        return false;
      }
      
      // 3. Sinon -> motif requis (sortie anticipée)
      return true;
      
    } catch (error) {
      console.error('Erreur dans verifierMotifNecessaire:', error);
      return true; // Par sécurité, on demande le motif en cas d'erreur
    }
  };

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    setSearch("");
    fetchEmployes(orgId);
    fetchPointages(1, orgId);
  };

  const fetchEmployes = async (orgId = selectedOrganisation) => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgId) params.append('organisation', orgId);
      
      const { data } = await api.get(`/employes/?${params}`);
      setEmployes(data);
      
      setCardsPagination({
        currentPage: 1,
        totalPages: Math.ceil(data.length / CARDS_PER_PAGE)
      });
    } catch (error) {
      console.error(error);
      Swal.fire({ 
        title: "Erreur", 
        text: "Impossible de charger les employés.", 
        icon: "error", 
        toast: true, 
        position: "top-right", 
        timer: 5000, 
        showConfirmButton: false 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPointages = async (targetPage = 1, orgId = selectedOrganisation) => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: targetPage,
        search,
        periode: periodeFilter,
        statut: statutFilter,
        date: selectedDate,
        organisation: orgId,
        page_size: pageSize === 'all' ? totalCount : pageSize
      });
      
      const response = await api.get(`/pointage/presences-today/?${params}`);
      setPointages(response.data.results);
      setTotalCount(response.data.count);
      
      if (pageSize === 'all') {
        setPagination({ totalPages: 1 });
      } else {
        setPagination({ totalPages: Math.ceil(response.data.count / pageSize) });
      }
      
      setPage(targetPage);
    } catch (error) {
      console.error("Erreur lors du chargement des pointages", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrganisation) {
      fetchEmployes();
      fetchPointages(1);
    }
  }, [selectedOrganisation, selectedDate, search, periodeFilter, statutFilter, pageSize]);

  const ouvrirModalConfirmation = async (employe, type) => {
    setEmployeSelectionne(employe);
    setTypePointage(type);
    
    const periode = determinerPeriode();
    setPeriodePointage(periode);
    
    // Vérifier si un motif est nécessaire avec la nouvelle logique
    if (type === 'sortie' && verifierMotifNecessaire()) {
      setMotifModal(true);
    } else {
      setConfirmationModal(true);
    }
  };

  const ouvrirModalModification = (pointage) => {
    setPointageSelectionne(pointage);
    setMotifSortie(pointage.motif_sortie || "");
    setModificationModal(true);
  };

  const ouvrirModalMotifDetail = (pointage) => {
    setMotifDetailModal({
      show: true,
      name: `${pointage.employe_prenom} ${pointage.employe_nom}`,
      motif: pointage.motif_sortie,
    });
  };

  const fermerModalMotifDetail = () => {
    setMotifDetailModal({ show: false, name: '', motif: '' });
  };

  const ouvrirModalImage = (pointage) => {
    setSelectedImage({
      src: pointage.photo,
      name: `${pointage.employe_prenom} ${pointage.employe_nom}`
    });
  };

  const fermerModalImage = () => {
    setSelectedImage(null);
  };

  const confirmerPointage = async () => {
    if (!employeSelectionne) return;
    
    setConfirmationModal(false);
    
    try {
      const endpoint = typePointage === 'entree' 
        ? '/pointage/entree/' 
        : '/pointage/sortie/';
      
      const formData = new FormData();
      formData.append('matricule', employeSelectionne.matricule);
      
      if (typePointage === 'sortie' && verifierMotifNecessaire() && motifSortie) {
        formData.append('motif_sortie_exceptionnelle', motifSortie);
      }
      
      formData.append('periode', periodePointage);
      
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMotifSortie("");
      fetchPointages(); 
      
      Swal.fire({ 
        title: "Succès", 
        text: response.data.message, 
        icon: "success", 
        toast: true, 
        position: "top-right", 
        timer: 5000, 
        showConfirmButton: false 
      });

    } catch (error) {
      console.error(error);
      const message = error.response?.data?.error || error.response?.data?.message || "Erreur lors du pointage";
      
      if (error.response?.status === 400 && message.includes("motif requis")) {
        Swal.fire({ 
          title: "Motif requis", 
          text: "Veuillez saisir un motif pour cette sortie.", 
          icon: "warning", 
          toast: true, 
          position: "top-right", 
          timer: 5000, 
          showConfirmButton: false 
        });
        setMotifModal(true);
        return;
      }
      
      Swal.fire({ 
        title: "Erreur", 
        text: message, 
        icon: "error", 
        toast: true, 
        position: "top-right", 
        timer: 5000, 
        showConfirmButton: false 
      });
    }
  };

  const modifierPointage = async () => {
    if (!pointageSelectionne) return;
    
    try {
      const response = await api.put(`/pointage/modifier/${pointageSelectionne.id}/`, {
        heure: pointageSelectionne.heure,
        motif_sortie: motifSortie,
        periode: pointageSelectionne.periode,
        statut: pointageSelectionne.statut
      });

      setModificationModal(false);
      setMotifSortie("");
      fetchPointages(); 
      
      Swal.fire({ 
        title: "Succès", 
        text: response.data.message, 
        icon: "success", 
        toast: true, 
        position: "top-right", 
        timer: 5000, 
        showConfirmButton: false 
      });

    } catch (error) {
      console.error(error);
      const message = error.response?.data?.error || "Erreur lors de la modification";
      
      Swal.fire({ 
        title: "Erreur", 
        text: message, 
        icon: "error", 
        toast: true, 
        position: "top-right", 
        timer: 5000, 
        showConfirmButton: false 
      });
    }
  };


const supprimerPointage = async (id) => {
  const result = await Swal.fire({
    title: "Confirmer la suppression",
    text: "Voulez-vous vraiment supprimer ce pointage ?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc3545",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Oui, supprimer",
    cancelButtonText: "Annuler"
  });

  if (!result.isConfirmed) return;

  try {
    await api.delete(`/pointage/supprimer/${id}/`);
    fetchPointages();
    Swal.fire({
      title: "Supprimé",
      text: "Pointage supprimé avec succès.",
      icon: "success",
      toast: true,
      position: "top-right",
      timer: 3000,
      showConfirmButton: false
    });
  } catch (error) {
    Swal.fire({
      title: "Erreur",
      text: error.response?.data?.error || "Erreur lors de la suppression.",
      icon: "error",
      toast: true,
      position: "top-right",
      timer: 5000,
      showConfirmButton: false
    });
  }
};






  const validerMotif = () => {
    if (!motifSortie.trim() && verifierMotifNecessaire()) {
      Swal.fire({ 
        title: "Motif requis", 
        text: "Veuillez saisir un motif pour la sortie.", 
        icon: "warning", 
        toast: true, 
        position: "top-right", 
        timer: 3000, 
        showConfirmButton: false 
      });
      return;
    }
    
    setMotifModal(false);
    setConfirmationModal(true);
  };

  const employesFiltres = employes.filter(emp =>
    `${emp.first_name} ${emp.last_name} ${emp.matricule}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const formatHeure = (heure) => {
    if (!heure) return '-';
    return heure.split('.')[0];
  };

  const generatePageSizeOptions = () => {
    const options = [10, 20, 30, 50]; 
    
    if (totalCount > 50 && !options.includes(totalCount)) {
      options.push(totalCount);
    }
    
    options.push('all');
    
    return options;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) fetchPointages(newPage);
  };

  const handleCardsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= cardsPagination.totalPages) {
      setCardsPagination({
        ...cardsPagination,
        currentPage: newPage
      });
    }
  };

  const getCurrentCards = () => {
    const startIndex = (cardsPagination.currentPage - 1) * CARDS_PER_PAGE;
    const endIndex = startIndex + CARDS_PER_PAGE;
    return employesFiltres.slice(startIndex, endIndex);
  };

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>
      {/* Indicateur de chargement des paramètres */}
      {loadingParametres && (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          Chargement des paramètres de pointage...
        </div>
      )}

      {/* Modal pour l'image agrandie */}
      {selectedImage && (
        <div style={styles.modalOverlay} onClick={fermerModalImage}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={fermerModalImage}>
              <FaTimes />
            </button>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.name}
              style={styles.modalImage}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/default-avatar.png';
              }}
            />
            <div style={styles.employeeName}>{selectedImage.name}</div>
          </div>
        </div>
      )}

      {/* Modal pour afficher le motif */}
      {motifDetailModal.show && (
        <div style={styles.modalOverlay} onClick={fermerModalMotifDetail}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={fermerModalMotifDetail}>
              <FaTimes />
            </button>
            
            <h4 style={{ color: "#1B019B", marginBottom: "15px" }}>
              Motif de sortie - {motifDetailModal.name}
            </h4>
            
            <p style={{ 
              fontSize: '1rem',
              textAlign: 'justify',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}>
              {motifDetailModal.motif || 'Aucun motif spécifié'}
            </p>
          </div>
        </div>
      )}

      {/* Header avec sélecteur d'organisation */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Pointage manuel pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <div style={styles.header}>
            <h3 style={styles.title}><FaUser /> Pointage Manuel - {currentOrganisation.nom}</h3>
          </div>

          {/* Cartes des employés pour le pointage */}
          <div style={styles.grid}>
            {getCurrentCards().map(emp => (
              <div key={emp.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <h4 style={styles.cardTitle}>{emp.first_name} {emp.last_name}</h4>
                    <p style={styles.cardText}>Matricule: {emp.matricule}</p>
                    <p style={styles.cardText}>Poste: {emp.poste}</p>
                  </div>
                </div>
                
                <div style={styles.cardActions}>
                  {(user?.is_super_admin || user?.permissions?.includes("ENTREE_POINTAGE_MANUEL")) && (
                  <button 
                    style={styles.entreeBtn} 
                    onClick={() => ouvrirModalConfirmation(emp, 'entree')}
                    onMouseEnter={(e) => e.target.style.background = "#218838"}
                    onMouseLeave={(e) => e.target.style.background = "#28a745"}
                  >
                    <FaSignInAlt /> Entrée
                  </button>
                  )}
                  {(user?.is_super_admin || user?.permissions?.includes("SORTIE_POINTAGE_MANUEL")) && (
                  <button 
                    style={styles.sortieBtn} 
                    onClick={() => ouvrirModalConfirmation(emp, 'sortie')}
                    onMouseEnter={(e) => e.target.style.background = "#c82333"}
                    onMouseLeave={(e) => e.target.style.background = "#dc3545"}
                  >
                    <FaSignOutAlt /> Sortie
                  </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination des cartes */}
          {cardsPagination.totalPages > 1 && (
            <div style={styles.cardsPagination}>
              <button
                style={cardsPagination.currentPage === 1 ? styles.paginationDisabled : styles.paginationButton}
                onClick={() => handleCardsPageChange(cardsPagination.currentPage - 1)}
                disabled={cardsPagination.currentPage === 1}
              >
                Précédent
              </button>
              
              <span style={styles.paginationInfo}>
                Page {cardsPagination.currentPage} sur {cardsPagination.totalPages}
              </span>
              
              <button
                style={cardsPagination.currentPage === cardsPagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                onClick={() => handleCardsPageChange(cardsPagination.currentPage + 1)}
                disabled={cardsPagination.currentPage === cardsPagination.totalPages}
              >
                Suivant
              </button>
            </div>
          )}

          {/* Filtres pour le tableau */}
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <FaSearch style={styles.filterIcon} />
              <input
                type="text"
                placeholder="Rechercher nom, prénom, période..."
                style={styles.searchBar}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={styles.filterGroup}>
              <FaCalendarAlt style={styles.filterIcon} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={styles.select}
              />
            </div>
            <div style={styles.filterGroup}>
              <FaClock style={styles.filterIcon} />
              <select
                style={styles.select}
                value={periodeFilter}
                onChange={(e) => setPeriodeFilter(e.target.value)}
              >
                <option value="">Toutes périodes</option>
                <option value="matin">Matin</option>
                <option value="apres_midi">Après-midi</option>
                <option value="pause">Pause</option>
                <option value="descente">Descente</option>
                <option value="autre">Exceptionnelle</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <FiAlertTriangle style={styles.filterIcon} />
              <select
                style={styles.select}
                value={statutFilter}
                onChange={(e) => setStatutFilter(e.target.value)}
              >
                <option value="">Tous statuts</option>
                <option value="PRESENT">Présent</option>
                <option value="RETARD">Retard</option>
                <option value="ABSENT">Absent</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.pageSizeLabel}>Afficher :</label>
              <select
                style={styles.select}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                }}
                disabled={loading}
              >
                {generatePageSizeOptions().map((size, idx) => (
                  <option key={idx} value={size}>
                    {size === 'all' ? 'Tous afficher' : `${size} lignes`}
                  </option>
                ))}
              </select>
            </div>
            {(user?.is_super_admin || user?.permissions?.includes("CREATE_POINTAGE_MANUEL")) && (
            <button
              style={{ backgroundColor: "#1B019B", color: "#fff", padding: "15px 16px", margintop: "15px" }}
              onClick={() => setAjoutModal(true)}
            >
              Ajouter un pointage
            </button>
            )}
          </div>

          {/* Tableau des pointages existants */}
          <div style={styles.tableContainer}>
            <h4 style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaHistory /> Pointages enregistrés pour le {new Date(selectedDate).toLocaleDateString()}
            </h4>
            
            {pointages.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Photo</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Nom</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Prénom</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Heure</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Type</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Période</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Statut</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Motif sortie</th>
                      
                      <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pointages.map((pointage) => (
                      <tr key={pointage.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={styles.photoCell}>
                          {pointage.photo ? (
                            <img
                              src={pointage.photo}
                              alt={`${pointage.employe_prenom} ${pointage.employe_nom}`}
                              style={styles.photo}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/default-avatar.png';
                              }}
                              onClick={() => ouvrirModalImage(pointage)}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                              }}
                            />
                          ) : (
                            <div style={styles.defaultPhoto}>
                              {/* Icône par défaut */}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>{pointage.employe_nom || '-'}</td>
                        <td style={{ padding: '12px' }}>{pointage.employe_prenom || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{pointage.date || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{formatHeure(pointage.heure)}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {pointage.type_pointage === 'entree' ? (
                            <>
                              <FaSignInAlt style={styles.entryIcon} />
                              Entrée
                            </>
                          ) : pointage.type_pointage === 'sortie' ? (
                            <>
                              <FaSignOutAlt style={styles.exitIcon} />
                              Sortie
                            </>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {pointage.periode === "autre" ? (
                            <span title={pointage.motif_sortie || 'Sortie hors période normale'} style={styles.exceptionnellePeriod}>
                              <FiAlertTriangle /> Exceptionnelle
                            </span>
                          ) : pointage.periode === "matin" ? (
                            <span style={styles.matinPeriod}>
                              <FiSunrise /> Matin
                            </span>
                          ) : pointage.periode === "apres_midi" ? (
                            <span style={styles.apresMidiPeriod}>
                              <FiSunset /> Après-midi
                            </span>
                          ) : pointage.periode === "descente" ? (
                            <span style={styles.descentePeriod}>
                              <FiArrowDownCircle /> Descente
                            </span>
                          ) : pointage.periode === "pause" ? (
                            <span style={styles.pausePeriod}>
                              <FiCoffee /> Pause
                            </span>
                          ) : (
                            <span style={styles.defaultPeriod}>
                              {pointage.periode?.charAt(0).toUpperCase() + pointage.periode?.slice(1).replace('_', ' ') || '-'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {pointage.statut ? (
                            <span
                              className={`badge ${
                                pointage.statut === "PRESENT"
                                  ? "bg-success"
                                  : pointage.statut === "RETARD"
                                  ? "bg-warning"
                                  : "bg-danger"
                              } rounded-pill`}
                            >
                              {pointage.statut}
                            </span>
                          ) : (
                            "-" 
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {pointage.motif_sortie ? (
                            <button
                              onClick={() => ouvrirModalMotifDetail(pointage)}
                              style={styles.motifButton}
                            >
                              <FaInfoCircle /> Détail
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>

                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button 
                            style={styles.editButton}
                            onClick={() => ouvrirModalModification(pointage)}
                            onMouseEnter={(e) => e.target.style.background = "#e0a800"}
                            onMouseLeave={(e) => e.target.style.background = "#ffc107"}
                          >
                            <FaEdit />
                          </button>


    <button
      style={{ ...styles.sortieBtn, padding: '6px 10px' }}
      onClick={() => supprimerPointage(pointage.id)}
      onMouseEnter={(e) => e.target.style.background = "#c82333"}
      onMouseLeave={(e) => e.target.style.background = "#dc3545"}
    >
      <FaTimes />
    </button>

                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={styles.noData}>Aucun pointage enregistré pour cette date.</p>
            )}

            {/* Pagination du tableau */}
            {pagination.totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                  onClick={() => handlePageChange(1)}
                  disabled={page === 1 || loading}
                >
                  Première
                </button>
                <button
                  style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1 || loading}
                >
                  &laquo;
                </button>

                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pagination.totalPages || loading}
                >
                  &raquo;
                </button>
                <button
                  style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={page === pagination.totalPages || loading}
                >
                  Dernière
                </button>
              </div>
            )}
          </div>

          {/* Modal de Saisie du Motif */}
          {motifModal && employeSelectionne && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <button onClick={() => {
                  setMotifModal(false);
                  setMotifSortie("");
                }} style={styles.closeBtn}>
                  <FaTimes />
                </button>
                
                <h4 style={{ color: "#1B019B", marginBottom: "15px" }}>
                  Motif de sortie exceptionnelle
                </h4>
                
                <p style={{ marginBottom: "10px" }}>
                  Cette sortie nécessite un motif. Veuillez saisir le motif pour :
                </p>
                
                <p style={{ fontWeight: "600", marginBottom: "5px" }}>
                  {employeSelectionne.first_name} {employeSelectionne.last_name}
                </p>
                
                <p style={{ color: "#555", marginBottom: "20px" }}>
                  Matricule: {employeSelectionne.matricule}
                </p>

                <textarea
                  style={styles.textarea}
                  placeholder="Saisissez le motif de la sortie exceptionnelle..."
                  value={motifSortie}
                  onChange={(e) => setMotifSortie(e.target.value)}
                  required
                />

                <div style={styles.modalActions}>
                  <button 
                    style={styles.confirmBtn}
                    onClick={validerMotif}
                    onMouseEnter={(e) => e.target.style.background = "#218838"}
                    onMouseLeave={(e) => e.target.style.background = "#28a745"}
                  >
                    Valider
                  </button>
                  
                  <button 
                    style={styles.cancelBtn}
                    onClick={() => {
                      setMotifModal(false);
                      setMotifSortie("");
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#c82333"}
                    onMouseLeave={(e) => e.target.style.background = "#dc3545"}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Modification */}
          {modificationModal && pointageSelectionne && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <button onClick={() => {
                  setModificationModal(false);
                  setMotifSortie("");
                }} style={styles.closeBtn}>
                  <FaTimes />
                </button>
                
                <h3 style={{ color: "#1B019B", marginBottom: "15px" }}>
                  Modifier le pointage
                </h3>
                
                <p style={{ marginBottom: "10px" }}>
                  Modification du pointage pour :
                </p>
                
                <p style={{ fontWeight: "600", marginBottom: "5px" }}>
                  {pointageSelectionne.employe_prenom} {pointageSelectionne.employe_nom}
                </p>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', textAlign: 'left', marginBottom: '5px', fontWeight: '600' }}>
                    Heure:
                  </label>
                  <input
                    type="time"
                    value={formatHeure(pointageSelectionne.heure)}
                    onChange={(e) => setPointageSelectionne({
                      ...pointageSelectionne,
                      heure: e.target.value + ':00'
                    })}
                    style={styles.inputField}
                  />
                </div>

                {pointageSelectionne.type_pointage === 'sortie' && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', textAlign: 'left', marginBottom: '5px', fontWeight: '600' }}>
                      Motif de sortie:
                    </label>
                    <textarea
                      style={styles.textarea}
                      placeholder="Saisissez le motif de la sortie..."
                      value={motifSortie}
                      onChange={(e) => setMotifSortie(e.target.value)}
                    />
                  </div>
                )}

                <div style={styles.modalActions}>
                  <button 
                    style={styles.confirmBtn}
                    onClick={modifierPointage}
                    onMouseEnter={(e) => e.target.style.background = "#218838"}
                    onMouseLeave={(e) => e.target.style.background = "#28a745"}
                  >
                    Enregistrer
                  </button>
                  
                  <button 
                    style={styles.cancelBtn}
                    onClick={() => {
                      setModificationModal(false);
                      setMotifSortie("");
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#c82333"}
                    onMouseLeave={(e) => e.target.style.background = "#dc3545"}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Confirmation */}
          {confirmationModal && employeSelectionne && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <button onClick={() => setConfirmationModal(false)} style={styles.closeBtn}>
                  <FaTimes />
                </button>
                
                <h3 style={{ color: "#1B019B", marginBottom: "15px" }}>
                  Confirmer le pointage
                </h3>
                
                <p style={{ marginBottom: "10px" }}>
                  Êtes-vous sûr de vouloir enregistrer une {typePointage === 'entree' ? 'entrée' : 'sortie'} pour :
                </p>
                
                <p style={{ fontWeight: "600", marginBottom: "5px" }}>
                  {employeSelectionne.first_name} {employeSelectionne.last_name}
                </p>
                
                <p style={{ color: "#555", marginBottom: "20px" }}>
                  Matricule: {employeSelectionne.matricule}
                </p>

                <p style={{ marginBottom: "10px", fontStyle: "italic" }}>
                  Période: {periodePointage}
                </p>

                {typePointage === 'sortie' && verifierMotifNecessaire() && motifSortie && (
                  <div style={{ textAlign: 'left', marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <p style={{ fontWeight: '600', marginBottom: '5px' }}>Motif de sortie:</p>
                    <p style={{ color: '#555', fontStyle: 'italic' }}>{motifSortie}</p>
                  </div>
                )}

                <div style={styles.modalActions}>
                  <button 
                    style={styles.confirmBtn}
                    onClick={confirmerPointage}
                    onMouseEnter={(e) => e.target.style.background = "#218838"}
                    onMouseLeave={(e) => e.target.style.background = "#28a745"}
                  >
                    Oui
                  </button>
                  
                  <button 
                    style={styles.cancelBtn}
                    onClick={() => {
                      setConfirmationModal(false);
                      setMotifSortie("");
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#c82333"}
                    onMouseLeave={(e) => e.target.style.background = "#dc3545"}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal Ajout Pointage */}
          {ajoutModal && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <button onClick={() => setAjoutModal(false)} style={styles.closeBtn}>
                  <FaTimes />
                </button>

                <h3 style={{ color: "#1B019B", marginBottom: "15px" }}>Ajouter un pointage</h3>

                {/* Employé */}
                <div style={{ marginBottom: '10px' }}>
                  <label>Employé:</label>
                  <select
                    value={nouveauPointage.employe}
                    onChange={(e) => setNouveauPointage({ ...nouveauPointage, employe: e.target.value })}
                    style={styles.select}
                  >
                    <option value="">Sélectionnez un employé</option>
                    {employes.map(emp => (
                      <option key={emp.id} value={emp.matricule}>
                        {emp.first_name} {emp.last_name} ({emp.matricule})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type Pointage */}
                <div style={{ marginBottom: '10px' }}>
                  <label>Type:</label>
                  <select
                    value={nouveauPointage.type_pointage}
                    onChange={(e) => setNouveauPointage({ ...nouveauPointage, type_pointage: e.target.value })}
                    style={styles.select}
                  >
                    <option value="entree">Entrée</option>
                    <option value="sortie">Sortie</option>
                  </select>
                </div>
{/* Ajouter après le champ Heure */}
<div style={{ marginBottom: '10px' }}>
  <label>Période :</label>
  <select
    value={nouveauPointage.periode}
    onChange={(e) => setNouveauPointage({ ...nouveauPointage, periode: e.target.value })}
    style={styles.select}
  >
    <option value="autre">Exceptionnelle</option>
    <option value="matin">Matin</option>
    <option value="apres_midi">Après-midi</option>
    <option value="pause">Pause</option>
    <option value="descente">Descente</option>
  </select>
</div>
                {/* Date */}
                <div style={{ marginBottom: '10px' }}>
                  <label>Date:</label>
                  <input
                    type="date"
                    value={nouveauPointage.date}
                    onChange={(e) => setNouveauPointage({ ...nouveauPointage, date: e.target.value })}
                    style={styles.select}
                  />
                </div>

                {/* Heure */}
                <div style={{ marginBottom: '10px' }}>
                  <label>Heure:</label>
                  <input
                    type="time"
                    value={nouveauPointage.heure}
                    onChange={(e) => setNouveauPointage({ ...nouveauPointage, heure: e.target.value })}
                    style={styles.inputField}
                  />
                </div>

                {/* Motif si sortie et période exceptionnelle */}
                {nouveauPointage.type_pointage === 'sortie' && verifierMotifNecessaire(nouveauPointage.heure + ':00') && (
                  <div style={{ marginBottom: '10px' }}>
                    <label>Motif de sortie exceptionnelle:</label>
                    <textarea
                      style={styles.textarea}
                      placeholder="Saisissez le motif de la sortie exceptionnelle..."
                      value={nouveauPointage.motif_sortie}
                      onChange={(e) => setNouveauPointage({ ...nouveauPointage, motif_sortie: e.target.value })}
                      required
                    />
                  </div>
                )}

                {/* Boutons */}
                <div style={styles.modalActions}>
                  <button
                    style={styles.confirmBtn}
                    onClick={async () => {
                      if (!nouveauPointage.employe) {
                        Swal.fire({
                          title: "Erreur",
                          text: "Veuillez sélectionner un employé.",
                          icon: "warning",
                          toast: true,
                          position: "top-right",
                          timer: 3000,
                          showConfirmButton: false
                        });
                        return;
                      }

                      if (nouveauPointage.type_pointage === 'sortie' && verifierMotifNecessaire(nouveauPointage.heure + ':00') && !nouveauPointage.motif_sortie.trim()) {
                        Swal.fire({
                          title: "Motif requis",
                          text: "Veuillez saisir un motif pour cette sortie exceptionnelle.",
                          icon: "warning",
                          toast: true,
                          position: "top-right",
                          timer: 3000,
                          showConfirmButton: false
                        });
                        return;
                      }

                      try {
                        const formData = new FormData();
                        Object.keys(nouveauPointage).forEach(key => formData.append(key, nouveauPointage[key]));

                        const response = await api.post('/pointage/pointage-ajouter/', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });

                        Swal.fire({
                          title: "Succès",
                          text: response.data.message,
                          icon: "success",
                          toast: true,
                          position: "top-right",
                          timer: 3000,
                          showConfirmButton: false
                        });

                        setAjoutModal(false);
                        setNouveauPointage({
                          employe: "",
                          type_pointage: "entree",
                          periode: "autre",
                          motif_sortie: "",
                          date: selectedDate,
                          heure: new Date().toISOString().slice(11, 16),
                        });
                        fetchPointages(1);
                      } catch (error) {
                        console.error(error);
                        Swal.fire({
                          title: "Erreur",
                          text: error.response?.data?.message || "Erreur lors de l'ajout du pointage.",
                          icon: "error",
                          toast: true,
                          position: "top-right",
                          timer: 5000,
                          showConfirmButton: false
                        });
                      }
                    }}
                  >
                    Enregistrer
                  </button>

                  <button
                    style={styles.cancelBtn}
                    onClick={() => setAjoutModal(false)}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default PointageManuelPage;